// New API format: token.balance_sheet.cached.balances[].{amount, block, tx}
// block and tx are decimal numbers (not hex strings like the old API)
import { ARBUZ_RUNE } from '../config/constants';
import { FIREBALL_IDS, DIESEL_DECIMALS } from '../fireball/fireballMath';

// Permissive mainnet BTC address SHAPE: a bc1 (segwit/taproot) or 1/3 (legacy)
// prefix of plausible length. Deliberately looser than the server-side strict
// BTC_ADDRESS_RE in api/* — its only job is to stop EMPTY / non-mainnet (tb1, bcrt1)
// / transient values from firing address-keyed react-query hooks that the alkanode
// proxy would then 400 (e.g. /api/alkanes-by-address). LaserEyes can briefly report
// connected=true with such an address, and `enabled: connected && address` lets any
// truthy string through. The proxy still does the strict validation upstream.
const MAINNET_ADDR_RE = /^(bc1[a-z0-9]{6,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;
export const isMainnetBtcAddress = (addr) => typeof addr === 'string' && MAINNET_ADDR_RE.test(addr);

export const isArbuzBalance = (b) =>
  b.block === ARBUZ_RUNE.BLOCK && b.tx === ARBUZ_RUNE.TX;

export const isArbuzToken = (token) =>
  token.balance_sheet?.cached?.balances?.some(isArbuzBalance) ?? false;

export const getArbuzBalanceEntry = (token) =>
  token.balance_sheet?.cached?.balances?.find(isArbuzBalance) ?? null;

export const getArbuzBalance = (token) => {
  const balances = token.balance_sheet?.cached?.balances?.filter(isArbuzBalance) ?? [];
  if (!balances.length) return 0;
  return balances.reduce((sum, b) => sum + b.amount, 0) / 1e8;
};

// ── DIESEL (alkanes genesis token, 2:0) — shown in the desktop header next to ARBUZ ──
export const isDieselBalance = (b) =>
  b.block === FIREBALL_IDS.DIESEL.block && b.tx === FIREBALL_IDS.DIESEL.tx;

export const isDieselToken = (token) =>
  token.balance_sheet?.cached?.balances?.some(isDieselBalance) ?? false;

export const getDieselBalance = (token) => {
  const balances = token.balance_sheet?.cached?.balances?.filter(isDieselBalance) ?? [];
  if (!balances.length) return 0;
  return balances.reduce((sum, b) => sum + b.amount, 0) / DIESEL_DECIMALS;
};

// adaptive precision: separators for big balances, decimals for small (mirrors the
// fireball fmtDiesel without pulling the arbContract module into the main bundle).
export const formatDiesel = (n) => {
  if (!Number.isFinite(n) || n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1000) return Math.round(n).toLocaleString('en-US');
  if (abs >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 3 });
  return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

export const formatArbuzId = (token) => {
  const b = getArbuzBalanceEntry(token);
  if (!b) return 'Unknown ID';
  return `${b.block}:${b.tx}`;
};

export const formatUtxo = (outpoint) =>
  outpoint
    ? `${outpoint.txid.slice(0, 8)}...${outpoint.txid.slice(-8)}:${outpoint.vout}`
    : 'N/A';
